import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  SyntheticEvent,
} from "react";
import {
  Box,
  Modal,
  IconButton,
  Select,
  MenuItem,
  Slider,
  SelectChangeEvent,
} from "@mui/material";
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { zoomOptions } from "../../../../Fixtures/ImageSuiteFixtureData";
interface ExpandGeneratedImageModalProps {
  open: boolean;
  imgSrc?: string;
  handleClose: () => void;
}

export const ExpandGeneratedImageModal = ({
  open,
  imgSrc,
  handleClose,
}: ExpandGeneratedImageModalProps) => {
  const [zoom, setZoom] = useState(100);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const startDrag = useRef<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const naturalSize = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  useEffect(() => {
    setPosition({ x: 0, y: 0 });
  }, [open, zoom]);

  const changeZoom = useCallback((value: number) => {
    setZoom(value);
  }, []);

  const zoomIn = useCallback(() => {
    const index = zoomOptions.indexOf(zoom);
    if (index < zoomOptions.length - 1) changeZoom(zoomOptions[index + 1]);
  }, [zoom, changeZoom]);

  const zoomOut = useCallback(() => {
    const index = zoomOptions.indexOf(zoom);
    if (index > 0) changeZoom(zoomOptions[index - 1]);
  }, [zoom, changeZoom]);

  const handleZoomSlider = useCallback(
    (_: Event, val: number | number[]) => {
      if (typeof val === "number") changeZoom(val);
    },
    [changeZoom]
  );

  const handleDropdownChange = useCallback(
    (e: SelectChangeEvent<number>) => {
      changeZoom(Number(e.target.value));
    },
    [changeZoom]
  );

  const clampPosition = (positionX: number, positionY: number) => {
    const container = containerRef.current;
    if (!container || zoom <= 100) return { x: 0, y: 0 };

    const rect = container.getBoundingClientRect();
    const { w, h } = naturalSize.current;
    if (!w || !h) return { x: 0, y: 0 };

    // size of the image before zoom
    const fitScale = Math.min(rect.width / w, rect.height / h);
    const baseW = w * fitScale;
    const baseH = h * fitScale;

    // size of the image after zoom
    const scale = zoom / 100;
    const scaledW = baseW * scale;
    const scaledH = baseH * scale;

    //part of the image that is outside the container
    const excessX = Math.max(0, (scaledW - rect.width) / 2);
    const excessY = Math.max(0, (scaledH - rect.height) / 2);

    // final clamped position
    // clamping the position to be within the excess area
    const clampedX = Math.min(Math.max(positionX, -excessX), excessX);
    const clampedY = Math.min(Math.max(positionY, -excessY), excessY);

    return { x: clampedX, y: clampedY };
  };

  const updatePosition = (positionX: number, positionY: number) => {
    setPosition(clampPosition(positionX, positionY));
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (zoom > 100) {
      setDragging(true);
      startDrag.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    }
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (dragging && startDrag.current) {
      updatePosition(
        e.clientX - startDrag.current.x,
        e.clientY - startDrag.current.y
      );
    }
  };

  const stopDrag = () => {
    setDragging(false);
    startDrag.current = null;
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (zoom > 100 && e.touches.length === 1) {
      const t = e.touches[0];
      setDragging(true);
      startDrag.current = {
        x: t.clientX - position.x,
        y: t.clientY - position.y,
      };
    }
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (dragging && startDrag.current && e.touches.length === 1) {
      const t = e.touches[0];
      updatePosition(
        t.clientX - startDrag.current.x,
        t.clientY - startDrag.current.y
      );
    }
  };
  const onTouchEnd = stopDrag;

  const handleWheel = (e: React.WheelEvent) => {
    if (!zoomOptions.length) return;
    e.preventDefault();
    const i = zoomOptions.indexOf(zoom);
    if (e.deltaY < 0 && i < zoomOptions.length - 1) {
      // Scroll up: zoom in
      changeZoom(zoomOptions[i + 1]);
    } else if (e.deltaY > 0 && i > 0) {
      // Scroll down: zoom out
      changeZoom(zoomOptions[i - 1]);
    }
  };

  const handleImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    naturalSize.current = { w: img.naturalWidth, h: img.naturalHeight };
  };

  return (
    <Modal open={open} onClose={handleClose} sx={{ opacity: 1 }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: "#22262F",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 50,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: "50%",
              right: 20,
              transform: "translateY(-50%)",
              color: "#fff",
              zIndex: 1000,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            width: "100%",
            overflow: "hidden",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: zoom > 100 ? (dragging ? "grabbing" : "grab") : "default",
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onWheel={handleWheel}
        >
          <img
            src={imgSrc}
            onLoad={handleImageLoad}
            alt="Generated"
            draggable={false}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${
                position.y
              }px)) scale(${zoom / 100})`,
              transition: dragging ? "none" : "transform 0.15s ease-out",
              width: "100%",
              height: "100%",
              objectFit: "contain",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            my: 2,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#191B22",
              display: "flex",
              alignItems: "center",
              p: 1,
              borderRadius: 1,
            }}
          >
            <IconButton onClick={zoomOut} disabled={zoom === zoomOptions[0]}>
              <ZoomOutIcon
                sx={{
                  color:
                    zoom === zoomOptions[0] ? "rgba(255,255,255,.4)" : "white",
                }}
              />
            </IconButton>
            <Slider
              value={zoom}
              min={zoomOptions[0]}
              max={zoomOptions.at(-1)!}
              step={null}
              marks={zoomOptions.map((z) => ({ value: z, label: "" }))}
              onChange={handleZoomSlider}
              sx={{
                width: 260,
                mx: 1,
                color: "#12B6EA",
                "& .MuiSlider-mark": { background: "transparent" },
              }}
            />
            <IconButton onClick={zoomIn} disabled={zoom === zoomOptions.at(-1)}>
              <ZoomInIcon
                sx={{
                  color:
                    zoom === zoomOptions.at(-1)
                      ? "rgba(255,255,255,.4)"
                      : "white",
                }}
              />
            </IconButton>
          </Box>
          <Select
            value={zoom}
            onChange={handleDropdownChange}
            sx={{
              width: 100,
              borderRadius: 1,
              backgroundColor: "#191B22",
              color: "white",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiSelect-icon": { color: "white" },
            }}
            MenuProps={{
              PaperProps: { sx: { bgcolor: "#191B22", color: "white" } },
            }}
          >
            {zoomOptions.map((value) => (
              <MenuItem key={value} value={value}>
                {value}%
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </Modal>
  );
};
